import * as Highcharts from "highcharts/highcharts-gantt";
import { format } from "date-fns";
import { convertDataToUTCFormat, ILD13SelectedLegend, LD13Chart, LD13ChartSeries } from "store/slice/charts";
import { transactionPopoverProps } from "./hooks/useTenure";
import { uniq } from "lodash";
import theme from "assets/styles/themes";
import { TenureChartTransaction } from "services/tenureChart";
import { TCustomiseLabelsOptions } from "../components/CustomiseLabels";

export const OP_FULL = 1;
export const OP_DIM = 0.2;

// Extend points so TS can see the LD13ChartSeries props it has
declare module "highcharts" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface PointOptionsObject extends Partial<LD13ChartSeries> {}

    interface Point extends Partial<LD13ChartSeries> {
        // Highcharts points may have a rendered graphic element, expose it as optional
        graphic?: Highcharts.SVGElement | undefined;
        dataLabel?: Highcharts.SVGElement;
        originalColor?: string | GradientColorObject | PatternObject | undefined;
    }
}

interface ExtendedGanttData extends LD13ChartSeries {
    dependency?: string;
    parent?: string;
    milestone?: boolean;
}

export interface GanttChartOptions extends Highcharts.Options {
    series?: Array<
        Highcharts.SeriesOptionsType & {
            data?: ExtendedGanttData[];
        }
    >;
}

export type ChartWithCustomLine = Highcharts.Chart & {
    customBottomLine?: Highcharts.SVGElement;
    customGridLines?: Highcharts.SVGElement[];
    currentFilteredPersonId?: string;
    pointColors?: Record<string, string>;
};

Highcharts.SVGRenderer.prototype.symbols.myCustomIcon = function (x: number, y: number, w: number, h: number) {
    return ["M", x, y + h / 2, "L", x + w / 2, y, "L", x + w, y + h / 2, "L", x + w / 2, y + h, "Z"];
};

const getYearStep = (minYear: number, maxYear: number) => {
    const spanYears = maxYear - minYear;

    if (spanYears <= 15) {
        return 1;
    }
    if (spanYears > 15 && spanYears <= 30) {
        return 2;
    }
    if (spanYears <= 50) {
        return 5;
    }

    return 10;
};

export const buildTransactionLine = (
    data: TenureChartTransaction,
    toggleTransactionsPopover: (popoverDetails: transactionPopoverProps) => void
): Highcharts.XAxisPlotLinesOptions => {
    return {
        id: `transaction-line`,
        value: Date.UTC(
            new Date(data.date).getUTCFullYear(),
            new Date(data.date).getUTCMonth(),
            new Date(data.date).getUTCDate()
        ),

        color: theme.palette.others.red,
        width: 2,
        dashStyle: "Dash" as const,
        zIndex: 7,
        label: {
            text: "",
        },
        events: {
            click: function (this, pointer) {
                const { x, y } = pointer as PointerEvent;
                toggleTransactionsPopover({ open: true, id: data.transaction_id, top: y, left: x });
            },
        },
    };
};

export const mapPersonDataById = (data: LD13Chart | undefined) => {
    const peopleById = new Map<string, { name: string; position?: string }>();

    data?.series?.forEach((p) => {
        if (!peopleById.has(p.personId)) {
            peopleById.set(p.personId, { name: p.name, position: p.role });
        }
    });

    return peopleById;
};

export const getUserLabel = (
    customiseLabels: TCustomiseLabelsOptions,
    personData: { name: string; position?: string } | undefined
): string => {
    let label = "";

    if (customiseLabels["show-names"]) {
        label += personData?.name ?? "";
    }

    if (customiseLabels["show-roles"]) {
        label += (label ? `, ${personData?.position ?? ""}` : personData?.position) ?? "";
    }

    return label;
};

export const labelFormatter = (
    formatterObj: Highcharts.AxisLabelsFormatterContextObject,
    customiseLabels: TCustomiseLabelsOptions,
    peopleById: ReturnType<typeof mapPersonDataById>
) => {
    const id = String(formatterObj.value ?? "");
    const person = peopleById.get(id);
    const label = getUserLabel(customiseLabels, person);

    const maxLen = 16;
    const short = (label.length ?? 0) > maxLen ? label.slice(0, maxLen) + "…" : label;

    return `<span title="${label}">${short}</span>`;
};

const buildYearTicks = (min: number, max: number) => {
    const minYear = new Date(min).getUTCFullYear();
    const maxYear = new Date(max).getUTCFullYear();
    const step = getYearStep(minYear, maxYear);

    const years: number[] = [];

    for (let y = minYear; y <= maxYear; y += step) {
        years.push(y);
    }

    // There is a chance that, depending on the date, the last year won't appear
    // This tweak it so we make sure to always show the last year
    if (years[years.length - 1] !== maxYear) {
        years.push(maxYear);
    }

    return years.map((y) => Date.UTC(y, 0, 1));
};

const COLUMNS_HEIGHT = 56; // 40 (Column height) + 16 (Y space)

interface getOptionsProps {
    data: LD13Chart | undefined;
    onClickSerie: (selectedLegend: ILD13SelectedLegend) => void;
    toggleTransactionsPopover: (popoverDetails: transactionPopoverProps) => void;
    yLabelFormatter: (formatterObj: Highcharts.AxisLabelsFormatterContextObject) => string;
}

export const getOptions = ({
    data,
    onClickSerie,
    toggleTransactionsPopover,
    yLabelFormatter,
}: getOptionsProps): GanttChartOptions => {
    const personIds = uniq(data?.series.map((p) => p.personId));

    const allPeopleHeight = (personIds.length ?? 0) * COLUMNS_HEIGHT;
    const chartHeight = Math.max(allPeopleHeight, 400);
    const transactions = data?.transactions.transactions || [];

    return {
        time: {
            useUTC: true,
        },
        chart: {
            spacing: [0, 0, 0, 0],
            height: chartHeight,
            style: {
                fontFamily: "Lato, sans-serif",
            },
            events: {
                render: function () {
                    const chart = this as ChartWithCustomLine;

                    // ------------------------------------------------
                    // Draw the Bottom line (before the dates)
                    // ------------------------------------------------
                    const y = chart.plotTop + chart.plotHeight + 0.5;
                    const lineLeft = 0;
                    const lineRight = chart.plotLeft + chart.plotWidth;

                    const path: Highcharts.SVGPathArray = [
                        ["M", lineLeft, y],
                        ["L", lineRight, y],
                    ];

                    if (chart.customBottomLine) {
                        chart.customBottomLine.attr({ d: path });
                    } else {
                        chart.customBottomLine = chart.renderer
                            .path(path)
                            .attr({
                                stroke: "#909090",
                                "stroke-width": 1,
                                zIndex: 7,
                            })
                            .add();
                    }

                    // --------------------------------------------------------------------
                    // Draw xAxis Custom grid lines (So they end before the Bottom line)
                    // --------------------------------------------------------------------
                    const axis = chart.xAxis[0];
                    const ticks = axis.tickPositions;

                    const top = chart.plotTop;
                    const bottom = chart.plotTop + chart.plotHeight;

                    const bottomLineY = bottom + 0.5;
                    const gridBottom = bottomLineY - 8;

                    // If no customGridLines, make it an empty array
                    if (!chart.customGridLines) {
                        chart.customGridLines = [];
                    }

                    // If chart changes size, destroy lines (so we create them again, easier than recalculating their position)
                    chart.customGridLines?.forEach((gl) => gl.destroy());
                    chart.customGridLines = [];

                    // Create the custom grid lines
                    ticks?.forEach((tick) => {
                        const x = axis.toPixels(tick, false);

                        const path: Highcharts.SVGPathArray = [
                            ["M", x, top],
                            ["L", x, gridBottom],
                        ];

                        const line = chart.renderer
                            .path(path)
                            .attr({
                                zIndex: 1,
                                stroke: "#DADEE5",
                                "stroke-width": 1,
                            })
                            .add();

                        // We save them so we can destroy or edit them later on
                        chart.customGridLines?.push(line);
                    });
                },
            },
        },
        title: {
            text: undefined,
        },
        legend: {
            enabled: false,
        },
        plotOptions: {
            gantt: {
                pointPadding: 0,
                groupPadding: 0,
                pointWidth: 40,
                borderRadius: 0,
                states: {
                    inactive: {
                        opacity: 0.2,
                    },
                    hover: {
                        enabled: false,
                    },
                },
            },
            series: {
                dataLabels: {
                    enabled: true,
                    format: "{point.totalTenure}",
                    align: "right",
                    x: -6,
                },
                point: {
                    events: {
                        click: function () {
                            const clicked = this as Highcharts.Point;
                            const clickedPersonId = clicked.personId;

                            if (clickedPersonId) {
                                onClickSerie({
                                    id: clickedPersonId,
                                    type: "personId",
                                });
                            }
                        },
                    },
                },
            },
        },
        xAxis: [
            // Axis to show year grid (here we hide the labels)
            {
                id: "yearGrid",
                opposite: false,
                currentDateIndicator: false,
                startOnTick: false,
                endOnTick: true,
                min: data?.xAxis.min ? convertDataToUTCFormat(data.xAxis.min) : 0,
                max: data?.xAxis.max ? convertDataToUTCFormat(data.xAxis.max) : 0,
                lineWidth: 0,
                gridLineWidth: 0,
                tickLength: 0,
                tickWidth: 0,
                plotLines: transactions.map((data) => {
                    return buildTransactionLine(data, toggleTransactionsPopover);
                }),
                labels: {
                    enabled: false,
                },
                tickPositioner: function (this) {
                    return buildYearTicks(this.min as number, this.max as number);
                },
            },
            // Axis to show the year labels (here we add all styles for the labels)
            {
                id: "yearLabels",
                linkedTo: 0,
                lineWidth: 0,
                gridLineWidth: 0,
                tickLength: 0,
                tickWidth: 0,
                labels: {
                    enabled: true,
                    format: "{value:%Y}",
                    style: {
                        color: "#454852",
                        fontSize: "10px",
                    },
                },
                tickPositioner: function (this) {
                    return buildYearTicks(this.min as number, this.max as number);
                },
            },
        ],
        yAxis: {
            uniqueNames: true,
            gridLineWidth: 0,
            lineWidth: 0,
            categories: personIds,
            title: {
                text: null,
            },
            labels: {
                enabled: true,
                format: "{value:%Y}",
                useHTML: true,
                formatter: yLabelFormatter,
                style: {
                    color: "#202020",
                    fontSize: "12px",
                    cursor: "default",
                },
            },
        },
        tooltip: {
            outside: false,
            backgroundColor: "#EBF6FF",
            borderRadius: 8,
            borderWidth: 0,
            style: {
                color: "#454852",
                fontSize: "12px",
                zIndex: "99999",
                width: 288,
                whiteSpace: "normal",
            },
            positioner: function (labelWidth, labelHeight, point) {
                const chart = this.chart;

                const x = Math.min(
                    chart.plotLeft + chart.plotWidth - labelWidth - 8,
                    Math.max(chart.plotLeft + 8, point.plotX + chart.plotLeft - labelWidth / 2)
                );

                const y = Math.max(chart.plotTop + 8, point.plotY + chart.plotTop - labelHeight - 12);

                return { x, y };
            },
            useHTML: true,
            followPointer: true,
            formatter: function () {
                const point = this.point as unknown as ExtendedGanttData;
                let outOfRange = "";

                const startDate = format(new Date(point.start), "MMM yyyy");
                const endDate = format(new Date(point.end), "MMM yyyy");
                const dateRange = [data?.xAxis.min, data?.xAxis.max];

                if (point.durationVisibility) {
                    outOfRange = `<div style="border-top: 1px solid #DADEE5; padding: 8px 0; margin: 8px 0 0 0;">
                    <div>Visible ${point.durationVisibility.type}: <strong>${format(
                        new Date(dateRange[0] ?? ""),
                        "MMM yyyy"
                    )}</strong></div>
                    <div>Duration Visible: <strong>${point.durationVisibility.duration}</strong></div></div>`;
                }

                if (point?.overlapingId) {
                    return `<div style="color: #454852; font-size: 14px; line-height: 18px; padding: 0; margin: 0;">
                                <div>Peers: <strong>${point.peers}</strong></div>
                                <div>Job Titles: <strong>${point.peersRoles}</strong></div>
                                <div>Dates: <strong>${startDate} - ${endDate}</strong></div>
                                <div>Company: <strong>${point.companyName}</strong></div>
                                ${outOfRange}
                            </div>`;
                }

                return `<div style="color: #454852; padding: 0 8px; min-width: 200px;">
                            <div style="font-size: 12px; margin: 4px 0 8px; padding-bottom: 8px; border-bottom: 1px solid #DADEE5">${point.name.toUpperCase()}</div>
                            <div style="font-size: 14px; line-height: 18px; padding: 0; margin: 0;">
                                <div>Job Title: <strong>${point.jobtitle}</strong></div>
                                <div>Start Date: <strong>${startDate}</strong></div>
                                <div>End Date: <strong>${endDate}</strong></div>
                                <div>Total Tenure: <strong>${point.totalTenure}</strong></div>
                                <div>Current Tenure: <strong>${point.currentTenure}</strong></div>
                                ${outOfRange}
                            </div>
                        </div>`;
            },
        },
        credits: {
            text: "",
        },
        series: [
            {
                name: "Work history",
                type: "gantt",
                xAxis: 0,
                data: data?.series,
                showInLegend: false,
            },
        ],
        exporting: {
            enabled: false,
        },
    };
};
